import { z } from 'zod';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, PHONE_REGEX, PINCODE_REGEX } from '../constants';

/**
 * Builds a Zod enum from one of our `as const` objects, keeping the literal
 * union type intact (z.nativeEnum only accepts real TS enums).
 */
export const zEnum = <T extends Record<string, string>>(e: T) =>
  z.enum(Object.values(e) as [T[keyof T], ...T[keyof T][]]);

export const idSchema = z.string().uuid();

export const phoneSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s-]/g, '').replace(/^(\+?91)/, ''))
  .pipe(z.string().regex(PHONE_REGEX, 'Enter a valid 10-digit Indian mobile number'));

export const emailSchema = z.string().trim().toLowerCase().email('Enter a valid email address');

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(72, 'Password must be at most 72 characters')
  .regex(/[A-Za-z]/, 'Password must contain a letter')
  .regex(/\d/, 'Password must contain a number');

export const pincodeSchema = z.string().regex(PINCODE_REGEX, 'Enter a valid 6-digit PIN code');

/** Accepts `YYYY-MM-DD` or an ISO datetime and normalises to a Date. */
export const dateSchema = z.coerce.date();

const TRUTHY = new Set(['true', '1', 'yes', 'y', 'on']);
const FALSY = new Set(['false', '0', 'no', 'n', 'off', '']);

/**
 * A boolean that survives a query string or a .env file.
 *
 * NOT `z.coerce.boolean()` — that applies JavaScript `Boolean()` semantics, so
 * every non-empty string is true. `?isBlocked=false` would parse as `true` and
 * return exactly the rows the caller asked to exclude, and `OTP_DEV_MODE=false`
 * would leave the dev OTP bypass switched on.
 *
 * Anything unrecognised is rejected rather than guessed at, so a typo surfaces
 * as a validation error instead of silently meaning `true`.
 */
export const booleanish = z
  .union([z.boolean(), z.string(), z.number()])
  .transform((value, ctx) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;

    const normalised = value.trim().toLowerCase();
    if (TRUTHY.has(normalised)) return true;
    if (FALSY.has(normalised)) return false;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Expected a boolean (true/false, 1/0, yes/no) but received "${value}"`,
    });
    return z.NEVER;
  });

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  sortBy: z.string().max(50).optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type PaginationQuery = z.infer<typeof paginationSchema>;

export const addressSchema = z.object({
  line1: z.string().trim().min(3).max(200),
  line2: z.string().trim().max(200).optional().or(z.literal('')),
  city: z.string().trim().min(2).max(100),
  district: z.string().trim().min(2).max(100),
  state: z.string().trim().min(2).max(100),
  pincode: pincodeSchema,
  landmark: z.string().trim().max(150).optional().or(z.literal('')),
});
export type AddressInput = z.infer<typeof addressSchema>;

export interface Paginated<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
  /** Field-level messages, keyed by dotted path — rendered inline by the web forms. */
  details?: Record<string, string[]>;
  requestId?: string;
}

/**
 * An optional number that a form may send as an empty string.
 *
 * `z.coerce.number()` turns '' into 0, so a field the user deliberately left
 * blank arrives as a real zero — which then fails a `.min()` and shows an
 * error on an input they never touched. Blank means absent.
 */
export const optionalNumber = (schema: z.ZodNumber) =>
  z.preprocess(
    (value) => (value === '' || value === null ? undefined : value),
    schema.optional(),
  );
